import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { squadTimeLabel } from "../../utility/createSquad";
import {
  formatSquadListDate,
  groupSquadsByDate,
  scoringHubSquadPath,
} from "../../utility/squadSchedule";
import {
  decorateSquadEventNames,
  squadEventLabel,
} from "../../utility/squadEventNames";
import {
  squadSessionLiveLabel,
  squadSessionStatus,
  SQUAD_SESSION,
} from "../../utility/squadSessionStatus";
import { useTournamentSlug } from "../../context/TournamentRouteContext";

const sessionEventLabel = (session) =>
  session.squads.map((squad) => squadEventLabel(squad, "Squad")).join(" · ");

const sharedEventLabel = (sessions) => {
  const labels = (sessions || []).map(sessionEventLabel).filter(Boolean);
  if (!labels.length) return "";
  return labels.every((label) => label === labels[0]) ? labels[0] : "";
};

const timeClass = (status) => {
  const base = "font-score text-xl tabular-nums whitespace-nowrap";
  if (status === SQUAD_SESSION.bowling) {
    return `${base} rounded px-1.5 bg-brand-gold text-brand-charcoal`;
  }
  if (status === SQUAD_SESSION.complete) {
    return `${base} text-brand-mist`;
  }
  return `${base} text-brand-charcoal`;
};

const SessionMark = ({ session, showEvent }) => {
  const tournamentSlug = useTournamentSlug();
  const timeLabel = squadTimeLabel(session.time);
  const eventLabel = showEvent ? sessionEventLabel(session) : "";
  const text = [timeLabel, eventLabel].filter(Boolean).join(" ");
  const status = squadSessionStatus(session.squads[0]);
  const liveLabel = squadSessionLiveLabel(status);
  const onlySquad = session.squads.length === 1 ? session.squads[0] : null;

  if (!text) return null;

  const mark = (
    <span className="inline-flex items-baseline gap-1.5">
      <span className={timeClass(status)}>{text}</span>
      {liveLabel ? (
        <span className="font-display font-bold tracking-wide text-sm text-brand-gold-ink">
          {liveLabel}
        </span>
      ) : null}
    </span>
  );

  if (onlySquad) {
    return (
      <Link
        to={scoringHubSquadPath(tournamentSlug, onlySquad.id)}
        className="hover:underline"
        aria-label={liveLabel ? `${text}, ${liveLabel}` : text}
      >
        {mark}
      </Link>
    );
  }

  return mark;
};

const TimeLine = ({ sessions, showEvent }) => (
  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
    {sessions.map((session, index) => (
      <React.Fragment key={session.time}>
        {index > 0 ? (
          <span className="text-brand-muted" aria-hidden="true">
            ·
          </span>
        ) : null}
        <SessionMark session={session} showEvent={showEvent} />
      </React.Fragment>
    ))}
  </div>
);

const HouseBlock = ({ house }) => {
  const sharedEvent = sharedEventLabel(house.sessions);
  const placeLine = [house.houseName, sharedEvent].filter(Boolean).join(" · ");
  return (
    <div>
      {placeLine ? (
        <p className="text-sm text-brand-muted">{placeLine}</p>
      ) : null}
      <TimeLine sessions={house.sessions} showEvent={!sharedEvent} />
    </div>
  );
};

const TournamentSquadsOverview = ({ tournamentId }) => {
  const [squads, setSquads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSquads = async () => {
      if (!tournamentId) return;
      try {
        setLoading(true);
        setError(null);
        const { data, error: fetchError } = await supabase
          .from("squads")
          .select(
            `
            id,
            squadDate,
            squadTime,
            squadLocation,
            bowlingCenters (
              id,
              centerName,
              formattedAddress,
              city,
              state,
              stateShort
            )
          `
          )
          .eq("tournamentID", tournamentId)
          .eq("isDeleted", false)
          .order("squadDate", { ascending: true })
          .order("squadTime", { ascending: true, nullsFirst: false });

        if (fetchError) throw fetchError;

        setSquads(await decorateSquadEventNames(data || []));
      } catch (err) {
        console.error("Error fetching squads:", err);
        setError("Failed to load squads");
      } finally {
        setLoading(false);
      }
    };

    fetchSquads();
  }, [tournamentId]);

  const heading = <h2 className="heading-display text-xl mb-4">Squads</h2>;

  if (loading) {
    return (
      <div className="card-board p-6">
        {heading}
        <div className="space-y-3 animate-pulse">
          <div className="h-4 bg-brand-charcoal/10 rounded w-1/2" />
          <div className="h-4 bg-brand-charcoal/10 rounded w-2/3" />
          <div className="h-4 bg-brand-charcoal/10 rounded w-1/3" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-board p-6">
        {heading}
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  const days = groupSquadsByDate(squads);

  if (days.length === 0) {
    return (
      <div className="card-board p-6">
        {heading}
        <p className="text-brand-muted text-sm">No squads have been added yet.</p>
      </div>
    );
  }

  return (
    <div className="card-board p-6">
      {heading}
      <div className="space-y-5">
        {days.map((day) => (
          <section
            key={day.key}
            className="border-t border-brand-charcoal/10 pt-4 first:border-t-0 first:pt-0"
          >
            <h3 className="heading-display text-base text-brand-charcoal">
              {formatSquadListDate(day.date)}
            </h3>
            <div className="mt-1 space-y-3">
              {day.houses.map((house) => (
                <HouseBlock key={house.key} house={house} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default TournamentSquadsOverview;
